import { Editor } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import cn from 'classnames';
import { ChangeEvent, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import richText from '../../assets/scss/richText.module.scss';
import { axiosInstance } from '../../helpers/axios';
import { ArticleVideo, VIDEO_CLASS } from './ArticleVideo';
import styles from './RichTextEditor.module.scss';
import { VideoDialog } from './VideoDialog';

// классы фото: их же пропускает бэкенд (app/article_content.py)
const PICTURE_CLASS = 'article-image';

// ровно то, что пропускает бэкенд (app/article_content.py): остальное он вырежет
const extensions = [
    StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
    }),
    // положение фото хранится классом, ширина стилем: их же пропускает бэкенд
    Image.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                class: { default: `${PICTURE_CLASS} ${PICTURE_CLASS}_full` },
                width: {
                    default: MAX_PICTURE_WIDTH,
                    parseHTML: (element) =>
                        parseInt(element.style.width, 10) || MAX_PICTURE_WIDTH,
                    renderHTML: (attributes) =>
                        String(attributes.class ?? '').includes(
                            PICTURE_PAGE_CLASS,
                        )
                            ? {}
                            : { style: `width:${attributes.width}%` },
                },
            };
        },
    }).configure({ inline: false, allowBase64: false }),
    ArticleVideo,
];

// бэкенд сжимает картинку до 1 Мб, но грузить многомегабайтный файл незачем
const MAX_PICTURE_SIZE = 10 * 1024 * 1024;

const MIN_PICTURE_WIDTH = 10;
const MAX_PICTURE_WIDTH = 100;

// положение задаётся одинаково фото и ролику, отличается только имя класса
const MEDIA_PLACES = [
    { label: 'Слева', hint: 'Слева от текста', name: 'left' },
    { label: 'Справа', hint: 'Справа от текста', name: 'right' },
    { label: 'Строкой', hint: 'Отдельной строкой', name: 'full' },
    { label: 'Во всю ширину', hint: 'Во всю ширину страницы', name: 'page' },
];

// у медиа во всю ширину ширину задаёт страница, поэтому свой стиль ему не нужен
const PICTURE_PAGE_CLASS = `${PICTURE_CLASS}_page`;

interface RichTextEditorProps {
    title: string;
    value: string;
    onChange: (html: string) => void;
    onBlur?: () => void;
    errorMessage?: string;
    wrapperClassName?: string;
    headerClassName?: string;
}

export const RichTextEditor = ({
    title,
    value,
    onChange,
    onBlur,
    errorMessage,
    wrapperClassName,
    headerClassName,
}: RichTextEditorProps) => {
    // редактор создаётся один раз, а обработчики формы между рендерами меняются
    const handlers = useRef({ onChange, onBlur });
    handlers.current = { onChange, onBlur };
    const pictureInput = useRef<HTMLInputElement>(null);
    const [isPictureLoading, setIsPictureLoading] = useState(false);
    const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);

    const editor = useEditor({
        extensions,
        content: value,
        editorProps: {
            attributes: {
                class: cn(richText.richText, styles.editor__content),
            },
        },
        // пустой редактор отдаёт '', а не <p></p>: так срабатывает «Поле обязательно»
        onUpdate: ({ editor }) =>
            handlers.current.onChange(editor.isEmpty ? '' : editor.getHTML()),
        onBlur: () => handlers.current.onBlur?.(),
    });

    const active = useEditorState({
        editor,
        selector: ({ editor }) => ({
            h2: editor.isActive('heading', { level: 2 }),
            h3: editor.isActive('heading', { level: 3 }),
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            bulletList: editor.isActive('bulletList'),
            orderedList: editor.isActive('orderedList'),
            blockquote: editor.isActive('blockquote'),
            link: editor.isActive('link'),
            image: editor.isActive('image'),
            video: editor.isActive('articleVideo'),
            mediaClass: String(
                (editor.isActive('image')
                    ? editor.getAttributes('image').class
                    : editor.getAttributes('articleVideo').class) ?? '',
            ),
            mediaWidth: Number(
                (editor.isActive('image')
                    ? editor.getAttributes('image').width
                    : editor.getAttributes('articleVideo').width) ??
                    MAX_PICTURE_WIDTH,
            ),
        }),
    });

    const editLink = () => {
        const url = window.prompt(
            'Адрес ссылки. Оставьте пустым, чтобы убрать ссылку',
            editor.getAttributes('link').href ?? '',
        );
        if (url === null) {
            return;
        }
        const chain = editor.chain().focus().extendMarkRange('link');
        if (url.trim()) {
            chain.setLink({ href: url.trim() }).run();
        } else {
            chain.unsetLink().run();
        }
    };

    // картинку сначала забирает бэкенд, в текст встаёт адрес сохранённого файла
    const addPicture = async (event: ChangeEvent<HTMLInputElement>) => {
        const picture = event.target.files?.[0];
        event.target.value = '';
        if (!picture) {
            return;
        }
        if (picture.size > MAX_PICTURE_SIZE) {
            toast.error('Фото больше 10 Мб');
            return;
        }
        const formData = new FormData();
        formData.append('image', picture);
        setIsPictureLoading(true);
        try {
            const { data } = await axiosInstance.post<{ url: string }>(
                '/v1/article/image/',
                formData,
            );
            editor.chain().focus().setImage({ src: data.url }).run();
        } catch {
            toast.error('Не получилось загрузить фото');
        } finally {
            setIsPictureLoading(false);
        }
    };

    // выбранное медиа: у фото и ролика свои имена узла и класса
    const mediaNode = active.image ? 'image' : 'articleVideo';
    const mediaClass = active.image ? PICTURE_CLASS : VIDEO_CLASS;

    // положение хранится классом, меняется только он
    const setMediaPlace = (name: string) => {
        const names = active.mediaClass.split(' ').filter(Boolean);
        const others = names.filter(
            (item) =>
                !MEDIA_PLACES.some(
                    (place) => `${mediaClass}_${place.name}` === item,
                ),
        );
        const next = [mediaClass, ...others, `${mediaClass}_${name}`];
        editor
            .chain()
            .focus()
            .updateAttributes(mediaNode, {
                class: next
                    .filter((item, index) => next.indexOf(item) === index)
                    .join(' '),
            })
            .run();
    };

    const setMediaWidth = (width: number) => {
        editor.chain().focus().updateAttributes(mediaNode, { width }).run();
    };

    const hasMediaClass = (name: string) =>
        active.mediaClass.split(' ').includes(`${mediaClass}_${name}`);

    const insertVideo = (src: string) => {
        setIsVideoDialogOpen(false);
        editor.chain().focus().insertContent({ type: 'articleVideo', attrs: { src } }).run();
    };

    const buttons = [
        {
            label: 'Заголовок',
            isActive: active.h2,
            run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        },
        {
            label: 'Подзаголовок',
            isActive: active.h3,
            run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
        },
        {
            label: 'Ж',
            hint: 'Жирный',
            className: styles.bold,
            isActive: active.bold,
            run: () => editor.chain().focus().toggleBold().run(),
        },
        {
            label: 'К',
            hint: 'Курсив',
            className: styles.italic,
            isActive: active.italic,
            run: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            label: 'Ч',
            hint: 'Подчёркнутый',
            className: styles.underline,
            isActive: active.underline,
            run: () => editor.chain().focus().toggleUnderline().run(),
        },
        {
            label: '• Список',
            isActive: active.bulletList,
            run: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
            label: '1. Список',
            isActive: active.orderedList,
            run: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
            label: 'Цитата',
            isActive: active.blockquote,
            run: () => editor.chain().focus().toggleBlockquote().run(),
        },
        { label: 'Ссылка', isActive: active.link, run: editLink },
        {
            label: isPictureLoading ? 'Загрузка…' : 'Фото',
            hint: 'Вставить фото в текст',
            isActive: active.image,
            run: () => pictureInput.current?.click(),
        },
        {
            label: 'Видео',
            hint: 'Вставить ролик в текст',
            isActive: active.video,
            run: () => setIsVideoDialogOpen(true),
        },
    ];

    return (
        <div className={cn(styles.editor, wrapperClassName)}>
            <h3 className={cn(styles.editor__header, headerClassName)}>
                {title}
            </h3>
            <div
                className={cn(styles.editor__box, {
                    [styles.editor__box_error]: errorMessage,
                })}
            >
                <div className={styles.editor__toolbar}>
                    {buttons.map((button) => (
                        <button
                            key={button.label}
                            type="button"
                            title={button.hint ?? button.label}
                            className={cn(
                                styles.editor__button,
                                button.className,
                                {
                                    [styles.editor__button_active]:
                                        button.isActive,
                                },
                            )}
                            // нажатие не должно уводить фокус из текста, иначе
                            // пропадает выделение, к которому применяется кнопка
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={button.run}
                        >
                            {button.label}
                        </button>
                    ))}
                </div>
                {/* панель висит над выбранным фото или роликом и не закрывает их */}
                <BubbleMenu
                    editor={editor}
                    className={styles.editor__picture}
                    options={{
                        placement: 'top',
                        offset: 12,
                        flip: true,
                        shift: { padding: 8 },
                    }}
                    shouldShow={({ editor }: { editor: Editor }) =>
                        editor.isActive('image') || editor.isActive('articleVideo')
                    }
                    updateDelay={0}
                    // по горизонтали панель привязана к окну редактора, а не
                    // к медиа: иначе при перетаскивании ползунка она дрожит
                    // вслед за меняющейся шириной фото
                    getReferencedVirtualElement={() => {
                        const media = editor.view.dom.querySelector(
                            '.ProseMirror-selectednode',
                        );
                        if (!media) {
                            return null;
                        }
                        const box = editor.view.dom.getBoundingClientRect();
                        const { top, height } = media.getBoundingClientRect();
                        return {
                            getBoundingClientRect: () =>
                                new DOMRect(box.left, top, box.width, height),
                        };
                    }}
                >
                    <div className={styles.editor__picture_inner}>
                        <span className={styles.editor__picture_title}>
                            {active.image ? 'Фото:' : 'Ролик:'}
                        </span>
                        {!hasMediaClass('page') ? (
                        <label className={styles.editor__width}>
                            Ширина
                            <input
                                className={styles.editor__width_slider}
                                type="range"
                                min={MIN_PICTURE_WIDTH}
                                max={MAX_PICTURE_WIDTH}
                                step={1}
                                value={active.mediaWidth}
                                onMouseDown={(event) => event.stopPropagation()}
                                onChange={(event) =>
                                    setMediaWidth(Number(event.target.value))
                                }
                            />
                            <span className={styles.editor__width_value}>
                                {active.mediaWidth}%
                            </span>
                        </label>
                        ) : null}
                        {MEDIA_PLACES.map((place) => (
                            <button
                                key={place.name}
                                type="button"
                                title={place.hint}
                                className={cn(styles.editor__button, {
                                    [styles.editor__button_active]:
                                        hasMediaClass(place.name),
                                })}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setMediaPlace(place.name)}
                            >
                                {place.label}
                            </button>
                        ))}
                    </div>
                </BubbleMenu>
                <EditorContent editor={editor} />
                <input
                    ref={pictureInput}
                    className={styles.editor__file}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    tabIndex={-1}
                    onChange={addPicture}
                />
            </div>
            {errorMessage ? (
                <span className={styles.editor__error}>{errorMessage}</span>
            ) : null}
            {isVideoDialogOpen ? (
                <VideoDialog
                    onInsert={insertVideo}
                    onClose={() => setIsVideoDialogOpen(false)}
                />
            ) : null}
        </div>
    );
};
